import React, { useState } from 'react';
import { Mail, Lock, LogIn } from 'lucide-react';
import { clsx } from 'clsx';
import { Button } from '@components/common/Button';
import { useLogin } from '@api/hooks/useLogin';
import { brand } from '@/config/brand';

interface LoginFormProps {
  onSuccess?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { mutate: login, isPending } = useLogin();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    login(
      { email, password },
      {
        onSuccess: () => {
          setEmail('');
          setPassword('');
          setErrors({});
          onSuccess?.();
        },
      }
    );
  };

  const inputClass = (hasError: boolean) =>
    clsx(
      'w-full pl-10 pr-3 py-2.5 rounded-lg text-sm transition-colors',
      'bg-[#0a1628] text-slate-100 placeholder:text-slate-500',
      'focus:outline-none focus:ring-2 focus:ring-offset-0',
      hasError
        ? 'border border-red-500/60 focus:ring-red-500/20'
        : 'border border-[#1e3a5f] focus:border-primary-500 focus:ring-primary-500/20'
    );

  return (
    <div className="w-full max-w-sm">
      {/* Logo + brand */}
      <div className="flex flex-col items-center mb-8">
        <img src={brand.logoUrl} alt={brand.name} className="h-12 w-12 mb-3" />
        <span className="text-lg font-semibold text-slate-200 tracking-tight">
          {brand.name}
        </span>
      </div>

      {/* Card */}
      <div className="bg-[#0d1f3c] border border-[#1e3a5f] rounded-xl shadow-xl p-8 space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-xl font-bold text-slate-100">Welcome back</h1>
          <p className="text-sm text-slate-400">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div className="space-y-1.5">
            <label htmlFor="email" className="block text-xs font-medium text-slate-400 uppercase tracking-wider">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors({ ...errors, email: '' });
                }}
                placeholder="you@example.com"
                className={inputClass(!!errors.email)}
                disabled={isPending}
              />
            </div>
            {errors.email && (
              <p className="text-xs text-red-400">{errors.email}</p>
            )}
          </div>

          {/* Password */}
          <div className="space-y-1.5">
            <label htmlFor="password" className="block text-xs font-medium text-slate-400 uppercase tracking-wider">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors({ ...errors, password: '' });
                }}
                placeholder="••••••••"
                className={inputClass(!!errors.password)}
                disabled={isPending}
              />
            </div>
            {errors.password && (
              <p className="text-xs text-red-400">{errors.password}</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={isPending || !email || !password}
            className="w-full mt-2 flex items-center justify-center gap-2"
          >
            <LogIn className="h-4 w-4" />
            {isPending ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-[#1e3a5f]" />
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">or</span>
          <div className="h-px flex-1 bg-[#1e3a5f]" />
        </div>

        {/* Microsoft SSO */}
        <a
          href="/api/sso/microsoft/login"
          className={clsx(
            'w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
            'bg-[#0a1628] text-slate-100 border border-[#1e3a5f]',
            'hover:bg-[#12233f] hover:border-primary-500/60',
            'focus:outline-none focus:ring-2 focus:ring-primary-500/20'
          )}
        >
          <MicrosoftIcon className="h-4 w-4" />
          Sign in with Microsoft
        </a>

        {/* Google SSO */}
        <a
          href="/api/sso/google/login"
          className={clsx(
            'w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
            'bg-[#0a1628] text-slate-100 border border-[#1e3a5f]',
            'hover:bg-[#12233f] hover:border-primary-500/60',
            'focus:outline-none focus:ring-2 focus:ring-primary-500/20'
          )}
        >
          <GoogleIcon className="h-4 w-4" />
          Sign in with Google
        </a>
      </div>
    </div>
  );
};

const MicrosoftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 21 21" className={className} aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const GoogleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 18 18" className={className} aria-hidden="true">
    <path
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
      fill="#4285F4"
    />
    <path
      d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      fill="#34A853"
    />
    <path
      d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
      fill="#FBBC05"
    />
    <path
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"
      fill="#EA4335"
    />
  </svg>
);

export default LoginForm;
